// Client for the local Oraclase laser agent (OraclaseWebGui) running on the
// same computer as the browser, default http://127.0.0.1:8080.
//
// Deliberately uses plain fetch() instead of the app-wide axios instance:
// axios attaches the cloud Authorization token to every request, which must
// never be sent to the local agent (and would break its CORS preflight).

const DEFAULT_AGENT_URL = "http://127.0.0.1:8080";

export function getAgentBaseUrl() {
    return localStorage.getItem("laserAgentUrl") || DEFAULT_AGENT_URL;
}

function agentHeaders(extra = {}) {
    const headers = { ...extra };
    const token = localStorage.getItem("laserAgentToken");
    if (token) {
        headers["X-Laser-Agent-Token"] = token;
    }
    return headers;
}

async function agentFetch(path, options = {}, timeoutMs = 0) {
    const controller = new AbortController();
    const timer = timeoutMs > 0 ? setTimeout(() => controller.abort(), timeoutMs) : null;
    try {
        const response = await fetch(`${getAgentBaseUrl()}${path}`, {
            ...options,
            headers: agentHeaders(options.headers || {}),
            signal: controller.signal,
        });
        let payload = null;
        try {
            payload = await response.json();
        } catch (parseError) {
            payload = null;
        }
        if (!response.ok) {
            const message = payload && payload.error ? payload.error : `Agent request failed (${response.status}).`;
            const error = new Error(message);
            error.status = response.status;
            throw error;
        }
        return payload;
    } finally {
        if (timer) clearTimeout(timer);
    }
}

// Probe the agent. Never throws: returns {available: false} when the agent is
// not running / not reachable, otherwise the full /api/status payload plus
// {available: true}.
export async function probeAgent({ timeoutMs = 2000 } = {}) {
    try {
        const status = await agentFetch("/api/status", { method: "GET" }, timeoutMs);
        return { available: true, ...status };
    } catch (error) {
        return { available: false, error: error.message };
    }
}

export function getProgress() {
    return agentFetch("/api/marking/progress", { method: "GET" }, 5000);
}

// Upload the marking JSON (as a Blob, so fetch sets Content-Length and the
// agent can stream it to disk). Returns the agent's summary payload.
export function uploadMarkingFile(blob, filename) {
    return agentFetch("/api/marking/summary", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            ...(filename ? { "X-Marking-Filename": filename } : {}),
        },
        body: blob,
    });
}

// Empty body: the agent marks the file previously stored via uploadMarkingFile.
export function startMarking() {
    return agentFetch("/api/marking/start", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: "{}",
    });
}

export function stopMarking() {
    return agentFetch("/api/marking/stop", { method: "POST" });
}

// Empty body: the agent traces the outline of the file previously stored via
// uploadMarkingFile with the guide laser, continuously until stopPreview.
export function startPreview() {
    return agentFetch("/api/marking/preview/start", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: "{}",
    });
}

export function stopPreview() {
    return agentFetch("/api/marking/preview/stop", { method: "POST" });
}
